import AddIcon from "@mui/icons-material/Add";
import { CloudUpload, Deselect, Send } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import BulkInvoiceActionContent, {
  BulkInvoiceAction,
} from "../../components/invoices/bulkInvoiceAction.component";
import InvoiceForm from "../../components/invoices/invoiceForm.component";
import CustomModal from "../../components/ui/modal/modal.component";
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { useInvoices } from "../../api/invoices";
import { useHasPermission } from "../../contexts/auth";
import {
  clearSelectedInvoices,
  useSelectedInvoices,
} from "../../hooks/useSelectedInvoices";
import { serviceActionsSectionStyle } from "../../styles/commonStyles";
import { Action } from "../../types/toolbar";
import { InvoiceSummary } from "../../types/invoices";

const QuickFilterCountAdornment = ({ count }: { count: number | null }) => (
  <Box
    sx={{
      width: 26,
      height: 20,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 12,
      fontWeight: 700,
      color: "text.primary",
      flexShrink: 0,
    }}
  >
    {count === null ? "..." : count}
  </Box>
);

export const InvoicesListActions = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [addInvoiceOpened, setAddInvoiceOpened] = useState<boolean>(false);
  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [bulkActionOpened, setBulkActionOpened] = useState<boolean>(false);
  const [bulkAction, setBulkAction] = useState<BulkInvoiceAction>("ksef");
  // Remounts the dialog content on every open, so a previous run's result
  // is not shown again.
  const [bulkActionKey, setBulkActionKey] = useState<number>(0);
  const canAddInvoice = useHasPermission("invoice.add_invoice");
  const canSend = useHasPermission("invoice.send_invoice");
  const canSubmitToKsef = useHasPermission("invoice.submit_invoice_to_ksef");
  const selectedInvoices = useSelectedInvoices();
  const { getSummary } = useInvoices();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const paidParam = params.get("is_paid") || "";
  const overdueParam = params.get("overdue") || "";

  const fetchSummary = useCallback(async () => {
    const data = await getSummary();
    if (data) {
      setSummary(data);
    }
  }, [getSummary]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  /**
   * Quick filters are written into the URL rather than component state, so a
   * filtered view survives a reload and can be shared as a link. The list page
   * reads them back out of `location.search`.
   */
  const applyFilter = (filter: { is_paid?: string; overdue?: string }) => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("is_paid");
    nextParams.delete("overdue");

    if (filter.is_paid) {
      nextParams.set("is_paid", filter.is_paid);
    }
    if (filter.overdue) {
      nextParams.set("overdue", filter.overdue);
    }

    const nextSearch = nextParams.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  };

  const isAll = !paidParam && !overdueParam;
  const isPaid = paidParam === "true" && !overdueParam;
  const isUnpaid = paidParam === "false" && !overdueParam;
  const isOverdue = overdueParam === "true";

  const totalCount = summary ? summary.total_count : null;

  const quickFilterActions: Action[] = [
    {
      icon: <QuickFilterCountAdornment count={totalCount} />,
      label: t("All invoices"),
      onClick: () => applyFilter({}),
      color: isAll ? "var(--primaryColor)" : undefined,
    },
    {
      icon: (
        <QuickFilterCountAdornment count={summary?.paid_count ?? null} />
      ),
      label: t("Paid"),
      onClick: () => applyFilter({ is_paid: "true" }),
      color: isPaid ? "var(--primaryColor)" : undefined,
    },
    {
      icon: (
        <QuickFilterCountAdornment count={summary?.unpaid_count ?? null} />
      ),
      label: t("Awaiting payment"),
      onClick: () => applyFilter({ is_paid: "false" }),
      color: isUnpaid ? "var(--primaryColor)" : undefined,
    },
    {
      icon: (
        <QuickFilterCountAdornment count={summary?.overdue_count ?? null} />
      ),
      label: t("Overdue"),
      onClick: () => applyFilter({ overdue: "true" }),
      color: isOverdue ? "var(--primaryColor)" : undefined,
    },
  ];

  const quickFilters = (
    <Box component="fieldset" sx={serviceActionsSectionStyle}>
      <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 1 }}>
        {t("Quick filters")}
      </Typography>
      <Toolbar actions={quickFilterActions} noTopPadding showShortcut={false} />
    </Box>
  );

  const openBulkAction = (action: BulkInvoiceAction) => {
    setBulkAction(action);
    setBulkActionKey((previous) => previous + 1);
    setBulkActionOpened(true);
  };

  const handleBulkActionQueued = () => {
    clearSelectedInvoices();
    window.dispatchEvent(new Event("invoiceUpdated"));
  };

  const selectionCount = selectedInvoices.length;

  const selectionActions: Action[] = [
    {
      icon: <CloudUpload />,
      label: t("Send to KSeF"),
      onClick: () => openBulkAction("ksef"),
      disabled: !canSubmitToKsef || selectionCount === 0,
    },
    {
      icon: <Send />,
      label: t("Send to clients"),
      onClick: () => openBulkAction("email"),
      disabled: !canSend || selectionCount === 0,
    },
    {
      icon: <Deselect />,
      label: t("Clear selection"),
      onClick: clearSelectedInvoices,
      disabled: selectionCount === 0,
    },
  ];

  const selectionSection = (canSend || canSubmitToKsef) && (
    <Box component="fieldset" sx={serviceActionsSectionStyle}>
      <Typography component="legend" variant="subtitle2" sx={{ px: 1, mb: 1 }}>
        {t("Selected invoices: {{count}}", { count: selectionCount })}
      </Typography>
      <Toolbar actions={selectionActions} noTopPadding showShortcut={false} />
    </Box>
  );

  const actions: Action[] = [
    {
      id: "new-invoice",
      icon: <AddIcon />,
      label: t("New invoice"),
      onClick: () => setAddInvoiceOpened(true),
      disabled: !canAddInvoice,
    },
  ];

  const handleInvoiceAdded = () => {
    setAddInvoiceOpened(false);
    fetchSummary();
    window.dispatchEvent(new Event("invoiceAdded"));
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          minHeight: 0,
        }}
      >
        {quickFilters}
        {selectionSection}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Toolbar actions={actions} noTopPadding />
        </Box>
      </Box>
      <CustomModal
        opened={addInvoiceOpened}
        setOpened={setAddInvoiceOpened}
        title={t("New invoice")}
        content={<InvoiceForm onSuccess={handleInvoiceAdded} />}
      />
      <CustomModal
        opened={bulkActionOpened}
        setOpened={setBulkActionOpened}
        title={
          bulkAction === "ksef"
            ? t("Send selected invoices to KSeF")
            : t("Send selected invoices to clients")
        }
        content={
          <BulkInvoiceActionContent
            key={bulkActionKey}
            action={bulkAction}
            invoices={selectedInvoices}
            onQueued={handleBulkActionQueued}
            onClose={() => setBulkActionOpened(false)}
          />
        }
      />
    </>
  );
};
